import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import logoGWPC from '../../assets/logo/gwpc-logo-transparent.png';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Simulate form submission
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setMessage('Thank you for your interest! We will contact you soon.');
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      setMessage('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: Mail, title: 'Email us', desc: 'contact@gwpc.com' },
    { icon: Phone, title: 'Call us', desc: '+1 (555) 123-4567' },
    { icon: MapPin, title: 'Visit us', desc: 'Headquarters Location' }
  ];

  // Shared field styling -- same recipe as Login.jsx.
  const fieldClass =
    'w-full rounded-lg border bg-white text-[15px] outline-none transition-all duration-150 placeholder:text-[#9aa7ba] focus:ring-[3px]';
  const fieldStyle = { borderColor: 'var(--line-3)' };
  const focusHandlers = {
    onFocus: (e) => { e.currentTarget.style.borderColor = 'var(--accent)'; },
    onBlur: (e) => { e.currentTarget.style.borderColor = 'var(--line-3)'; }
  };

  return (
    <div
      className="flex min-h-screen w-full items-center justify-center px-4 py-10"
      style={{ background: 'var(--bg)' }}
    >
      <div className="w-full max-w-[440px]">
        {/* Brand */}
        <div className="mb-7 flex flex-col items-center text-center">
          <img
            src={logoGWPC}
            alt="Great Workplace Club"
            className="h-auto w-[190px] object-contain"
          />
          <p className="mt-3 text-[13px]" style={{ color: 'var(--muted)' }}>
            Employee Engagement &amp; Survey Platform
          </p>
        </div>

        {/* Card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="rounded-xl p-7"
          style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow-sm)' }}
        >
          <div className="mb-5 text-center">
            <h2 className="text-[20px] font-semibold" style={{ color: 'var(--ink)' }}>Get Certified</h2>
            <p className="mt-1 text-[13px]" style={{ color: 'var(--muted)' }}>
              Interested in Great Workplace Club? Fill out the form and we&apos;ll get in touch.
            </p>
          </div>

          {/* Compact contact-info strip */}
          <div className="mb-5 grid grid-cols-3 gap-2">
            {contactInfo.map((item) => (
              <div
                key={item.title}
                className="flex flex-col items-center gap-1 rounded-lg px-2 py-3 text-center"
                style={{ background: 'var(--tint)' }}
              >
                <item.icon className="h-4 w-4" style={{ color: 'var(--accent)' }} />
                <span className="text-[10px] font-medium leading-tight" style={{ color: 'var(--tint-ink)' }}>{item.title}</span>
                <span className="text-[10px] leading-tight" style={{ color: 'var(--muted)' }}>{item.desc}</span>
              </div>
            ))}
          </div>

          {message && (
            <div className="mb-4 flex items-center rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              <CheckCircle2 className="mr-2 h-5 w-5 flex-shrink-0" />{message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>Full name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`${fieldClass} h-[42px] px-4`}
                style={{ ...fieldStyle, '--tw-ring-color': 'var(--accent)' }}
                {...focusHandlers}
                placeholder="Your full name"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>Email address</label>
              <div className="group relative">
                <Mail
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2"
                  style={{ color: 'var(--faint)' }}
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`${fieldClass} h-[42px] pl-12 pr-4`}
                  style={{ ...fieldStyle, '--tw-ring-color': 'var(--accent)' }}
                  {...focusHandlers}
                  placeholder="your.email@company.com"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>Company name</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className={`${fieldClass} h-[42px] px-4`}
                style={{ ...fieldStyle, '--tw-ring-color': 'var(--accent)' }}
                {...focusHandlers}
                placeholder="Your company name"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                className={`${fieldClass} resize-none px-4 py-2`}
                style={{ ...fieldStyle, '--tw-ring-color': 'var(--accent)' }}
                {...focusHandlers}
                placeholder="Tell us about your needs..."
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex h-[46px] w-full items-center justify-center gap-3 rounded-lg text-[15px] font-semibold text-white transition-all duration-150 disabled:pointer-events-none disabled:opacity-50"
              style={{ background: 'var(--accent)' }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = 'var(--accent-strong)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--accent)'; }}
            >
              {loading ? (
                <><div className="h-5 w-5 animate-spin rounded-full border-b-2 border-white" />Sending...</>
              ) : (
                <><Send className="h-5 w-5" />Send Message</>
              )}
            </button>
          </form>

          <div className="mt-5 text-center text-[13px]" style={{ borderTop: '1px solid var(--line)', paddingTop: '1rem', color: 'var(--body)' }}>
            Already have an account?{' '}
            <Link to="/login" className="font-medium" style={{ color: 'var(--accent)' }}>Sign in here</Link>
          </div>
        </motion.div>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-center gap-3 text-[12px]" style={{ color: 'var(--faint)' }}>
          <Link to="/contact" className="hover:underline">Privacy Policy</Link>
          <span>|</span>
          <Link to="/contact" className="hover:underline">Terms of Service</Link>
          <span>|</span>
          <Link to="/contact" className="hover:underline">Help &amp; Support</Link>
        </div>
      </div>
    </div>
  );
};

export default Contact;
